import React, { createContext, useContext, useState, useEffect } from "react";
import { adminApi } from "../services/api";
import toast from "react-hot-toast";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch admin profile from backend
  const fetchAdminProfile = async (token) => {
    try {
      const response = await adminApi.get("/auth/admin/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const adminProfile = response.data.data || response.data;

      // Store full admin profile
      localStorage.setItem("adminProfile", JSON.stringify(adminProfile));
      setAdmin(adminProfile);
      setIsAuthenticated(true);

      return adminProfile;
    } catch (error) {
      console.error("Error fetching admin profile:", error);
      // If profile fetch fails, clear everything
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminProfile");
      setAdmin(null);
      setIsAuthenticated(false);
      throw error;
    }
  };

  useEffect(() => {
    // Check if user is already logged in
    const initAuth = async () => {
      const token = localStorage.getItem("adminToken");
      const storedProfile = localStorage.getItem("adminProfile");

      if (token) {
        try {
          // Set auth header first
          adminApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;

          // Try to use stored profile first for faster load
          if (storedProfile) {
            const parsedProfile = JSON.parse(storedProfile);
            setAdmin(parsedProfile);
            setIsAuthenticated(true);
          }

          // Then fetch fresh profile in background
          await fetchAdminProfile(token);
        } catch (error) {
          console.error("Error initializing auth:", error);
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminProfile");
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);

      // Step 1: Login and get token
      const loginResponse = await adminApi.post("/auth/admin/login", {
        username,
        password,
      });

      const { token } = loginResponse.data;

      if (!token) {
        throw new Error("No token received from server");
      }

      // Store token
      localStorage.setItem("adminToken", token);

      // Set auth header for subsequent requests
      adminApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      // Step 2: Fetch full admin profile
      const adminProfile = await fetchAdminProfile(token);

      toast.success(
        `Welcome back, ${adminProfile.name || adminProfile.username}!`
      );
      return { success: true, admin: adminProfile };
    } catch (error) {
      console.error("Login error:", error);
      const message = error.response?.data?.message || "Login failed";
      toast.error(message);

      // Clean up on error
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminProfile");
      delete adminApi.defaults.headers.common["Authorization"];

      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminProfile");
    setAdmin(null);
    setIsAuthenticated(false);
    delete adminApi.defaults.headers.common["Authorization"];
    toast.success("Logged out successfully");
  };

  const updateAdmin = (adminData) => {
    setAdmin(adminData);
    localStorage.setItem("adminProfile", JSON.stringify(adminData));
  };

  const refreshProfile = async () => {
    const token = localStorage.getItem("adminToken");
    if (token) {
      try {
        return await fetchAdminProfile(token);
      } catch (error) {
        console.error("Error refreshing profile:", error);
        return null;
      }
    }
  };

  const value = {
    admin,
    user: admin, // Keep for backward compatibility
    isAuthenticated,
    loading,
    login,
    logout,
    updateAdmin,
    updateUser: updateAdmin, // Keep for backward compatibility
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
